import {useCallback, useEffect, useRef, useState} from 'react'
import apiClient from '../../helpers/apiClient'

// Infinite scroll para /users/search/ cuando NO hay filtros de búsqueda
// (dashboard discovery: sin `where`/`key` y sin categoría activa). El
// backend DRF usa LimitOffsetPagination y expone `count`, `next`, `results`
// por página. Aquí pedimos `limit=pageSize` y vamos moviendo `offset` hasta
// que la respuesta indica que no hay más. El caller es quien decide con
// `enabled` si este flujo aplica (ej. `!hasQuery && !hasCategory`).

export interface UseInfiniteMembersSearchResult {
  items: any[]
  loading: boolean
  hasMore: boolean
  error: Error | null
  loadMore: () => void
  /** Ref para el elemento sentinel que dispara la carga cuando entra al viewport. */
  sentinelRef: (node: HTMLElement | null) => void
  reset: () => void
}

interface Options {
  /** Si false, el hook no dispara ninguna carga. Útil para saltar el modo infinite cuando hay query. */
  enabled?: boolean
  /** Cuántos items pedir por página. Default 20. */
  pageSize?: number
  /**
   * Distancia (px) desde el borde inferior del viewport a la que empieza a cargar
   * la siguiente página. Un margen positivo evita que el usuario vea el spinner
   * al llegar al final; anticipa la carga.
   */
  rootMargin?: string
}

export const useInfiniteMembersSearch = ({
  enabled = true,
  pageSize = 20,
  rootMargin = '400px',
}: Options = {}): UseInfiniteMembersSearchResult => {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // Guardar en refs para leer valores actualizados dentro de callbacks sin
  // recrear observers cada render.
  const loadingRef = useRef(false)
  const hasMoreRef = useRef(true)
  const offsetRef = useRef(0)
  const enabledRef = useRef(enabled)

  useEffect(() => {
    loadingRef.current = loading
  }, [loading])
  useEffect(() => {
    hasMoreRef.current = hasMore
  }, [hasMore])
  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  const reset = useCallback(() => {
    setItems([])
    setLoading(false)
    setHasMore(true)
    setError(null)
    offsetRef.current = 0
    loadingRef.current = false
    hasMoreRef.current = true
  }, [])

  const loadMore = useCallback(async () => {
    if (!enabledRef.current) return
    if (loadingRef.current || !hasMoreRef.current) return

    loadingRef.current = true
    setLoading(true)
    setError(null)

    try {
      const params: Record<string, string | number> = {
        where: '',
        key: '',
        limit: pageSize,
        offset: offsetRef.current,
      }
      const {data} = await apiClient.get('/users/search/', {params})
      const results: any[] = Array.isArray(data?.results) ? data.results : []
      const nextHasMore = Boolean(data?.next) && results.length > 0

      setItems((prev) => {
        // Dedupe by id: en carreras de peticiones puede colarse un item repetido.
        const seen = new Set(prev.map((m) => String(m?.id)))
        const merged = [...prev]
        for (const m of results) {
          const id = String(m?.id ?? '')
          if (id && !seen.has(id)) {
            seen.add(id)
            merged.push(m)
          }
        }
        return merged
      })
      offsetRef.current += results.length
      hasMoreRef.current = nextHasMore
      setHasMore(nextHasMore)
    } catch (err) {
      setError(err as Error)
      hasMoreRef.current = false
      setHasMore(false)
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [pageSize])

  // Reset y primera carga cuando `enabled` cambia.
  useEffect(() => {
    if (!enabled) return
    reset()
    // Kick off inmediato (el sentinel puede tardar un frame en montarse).
    loadMore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  // IntersectionObserver bindeado a un ref-callback. Usar ref-callback (en vez de
  // useRef + useEffect) permite reconectar el observer si el nodo cambia (por
  // ejemplo cuando el sentinel se re-monta al cambiar de query -> sin query).
  const observerRef = useRef<IntersectionObserver | null>(null)
  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      if (!node || !enabled) return
      if (typeof IntersectionObserver === 'undefined') return

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const first = entries[0]
          if (first?.isIntersecting) loadMore()
        },
        {rootMargin}
      )
      observerRef.current.observe(node)
    },
    [enabled, loadMore, rootMargin]
  )

  // Cleanup al desmontar.
  useEffect(
    () => () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    },
    []
  )

  return {items, loading, hasMore, error, loadMore, sentinelRef, reset}
}
